/* eslint-disable local-rules/set-state-in-use-effect */
import {useConfig} from '@aboutyou/config';
import {useTabStorage} from '@aboutyou/storage';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useIsAuthenticated} from '../../features/Authentication/hooks/useIsAuthenticated';
import {HelpCenterChatbot} from './HelpCenterChatbot';
import {useHelpCenterChatbot} from './hooks/useHelpCenterChatbot';

enum HelpCenterChatbotState {
  Open = 'open',
  Show = 'show',
}

export const HelpCenterChatbotOpener: React.FC = () => {
  const {open, isOpen, isShown, show, close, hide} = useHelpCenterChatbot();
  const [mountChatbot, setMountChatbot] = useState(false);
  const {device} = useConfig();
  const tabStorageTimer = useRef<NodeJS.Timeout>(undefined);
  const {storageValue, updateTabStorage} = useTabStorage<{
    chatbotState?: HelpCenterChatbotState;
  }>();

  const {isUserDataLoading} = useIsAuthenticated();

  const helpCenterChatbotState = useMemo(() => {
    if (isOpen) {
      return HelpCenterChatbotState.Open;
    }

    if (isShown) {
      return HelpCenterChatbotState.Show;
    }

    return undefined;
  }, [isOpen, isShown]);

  // Stores in route state info that the chatbot state when the user reloads the page.
  // eslint-disable-next-line local-rules/too-complex-useeffects
  useEffect(() => {
    const updateRouteState = () => {
      window.history.replaceState(
        {...window.history.state, helpCenterChatbotState},
        '',
      );
    };

    window.addEventListener('beforeunload', updateRouteState);

    return () => {
      window.removeEventListener('beforeunload', updateRouteState);
    };
  }, [helpCenterChatbotState]);

  // Keep the previous chatbot state when the user reloads the page.
  useEffect(() => {
    const previousChatbotState = window.history.state?.helpCenterChatbotState;

    if (!previousChatbotState) {
      return;
    }

    if (isUserDataLoading) {
      return;
    }

    // setTimeout is used here to make sure that react-query has updated the cache when open is called.
    // Calling it immediately doesn't update the cache.
    setTimeout(
      previousChatbotState === HelpCenterChatbotState.Open ? open : show,
    );
  }, [isUserDataLoading]);

  // Stores the chatbot state in the TabStorageProvider.
  useEffect(() => {
    if (!mountChatbot || storageValue.chatbotState === helpCenterChatbotState) {
      return;
    }

    // eslint-disable-next-line local-rules/set-state-in-use-effect
    updateTabStorage({
      chatbotState: helpCenterChatbotState,
    });
  }, [
    helpCenterChatbotState,
    mountChatbot,
    storageValue.chatbotState,
    updateTabStorage,
  ]);

  // Maintain the same chatbot state across tabs.
  // eslint-disable-next-line local-rules/too-complex-useeffects
  useEffect(() => {
    // setTimeout is used here to make sure that react-query has updated the cache when open is called.
    // Calling it immediately doesn't update the cache.
    if (tabStorageTimer.current) {
      clearTimeout(tabStorageTimer.current);
    }

    tabStorageTimer.current = setTimeout(() => {
      switch (storageValue.chatbotState) {
        case HelpCenterChatbotState.Open: {
          // On mobile, the widget displays in full-screen mode, which might feel intrusive to the user.
          // To address this, it is automatically minimized.
          device === 'desktop' ? open() : show();
          break;
        }
        case HelpCenterChatbotState.Show: {
          if (helpCenterChatbotState === HelpCenterChatbotState.Open) {
            close();
          }

          show();
          break;
        }
        case undefined:
        default:
          hide();
          break;
      }
    }, 250);
  }, [helpCenterChatbotState, storageValue.chatbotState]);

  // Make sure that the chatbot is mounted on first open and never unmounts since the launcher stays rendered.
  useEffect(() => {
    if (isShown && !mountChatbot) {
      setMountChatbot(true);
    }
  }, [isShown, mountChatbot]);

  if (mountChatbot) {
    return <HelpCenterChatbot />;
  }

  return null;
};
